// src/components/TaxDepreciationSchedule.js
import React, { useState, useEffect } from 'react';
// Importing icons from react-icons
import { FaTimesCircle, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';
import Header from './Header';
import Footer from './Footer';

import {
  calculateCapitalWorksDepreciation,
} from '../../helpers/taxDepreciationHelpers';
import { getConstructionIndex } from '../../helpers/constructionIndexHelper'; // Ensure this helper is correctly implemented

const TaxDepreciationSchedule = ({ propertyValuation, pageNumber }) => {

	const propertyDetails = propertyValuation?.propertyAttributes?.propertyDetails;
	const actualValuation = propertyValuation?.propertyValuation?.actualValuation;
	const valuationSummary = propertyValuation?.propertyValuation?.valuationSummary;
	const taxDepreciationSchedule = propertyValuation?.taxDepreciationSchedule;
	const depreciationBreakdown = propertyValuation?.taxDepreciationSchedule.depreciationBreakdown;
    

    // Initialize state variables
    const [totalRebuildCost, setTotalRebuildCost] = useState(0);
    const [rebuildCostBreakdown, setRebuildCostBreakdown] = useState([]);
    const [constructionIndex, setConstructionIndex] = useState(100);
    const [latestConstructionIndex, setLatestConstructionIndex] = useState(100);
    
    const [indexedCapitalWorksCost, setIndexedCapitalWorksCost] = useState(0);
    const [annualDepreciation, setAnnualDepreciation] = useState(0);

    // **New State Variables for Depreciation Range**
    const [lowEndIndexedCost, setLowEndIndexedCost] = useState(0);
    const [highEndIndexedCost, setHighEndIndexedCost] = useState(0);
    const [annualDepreciationLow, setAnnualDepreciationLow] = useState(0);
    const [annualDepreciationHigh, setAnnualDepreciationHigh] = useState(0);

    // Currency formatter function for $xxx,xxx with no decimal places (AUD)
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    // Variance color function
    const getVarianceColor = (value) => {
        if (value > 0) return 'text-green-700'; // deep green
        if (value < 0) return 'text-red-700'; // deep red
        return ''; // no variance
    };

    // Define items that are excluded from Division 43
    const partialItems = ['Finishes - Wall', 'Finishes - Floor', 'Finishes - Ceiling', 'Fixtures'];
    const excludedItems = ['Solar panels', 'Swimming pool'];

    // Helper function to render inclusion status with icons
    const renderInclusionStatus = (inclusionStatus) => {
        let status = 'Included';
        let IconComponent = FaCheckCircle;
        let iconColor = 'text-green-500'; // Green for Included

        if (inclusionStatus == 'Included') {
            //Leave as defaults
        } else if (inclusionStatus == 'Excluded') {
            IconComponent = FaTimesCircle;
            iconColor = 'text-red-500'; // Red for Excluded
        } else if (inclusionStatus == 'Partial') {
            IconComponent = FaExclamationCircle;
            iconColor = 'text-amber-500'; // Amber for Partial
        }

        return (
            <div className="flex items-center justify-end">
                <IconComponent className={`inline mr-2 ${iconColor}`} />
                <span className="text-black">{inclusionStatus}</span>
            </div>
        );
    };

    return (
        <div className={`bg-gray-200 w-[794px] h-[1123px] flex flex-col page-break`}>
            <Header pageNumber={pageNumber} propertyDetails={propertyDetails} />
            
            {/* **End of Red Bar** */}
            <div className="flex-grow p-4">

                <h2 className="text-lg font-semibold mb-2">Division 43 Tax Depreciation Schedule</h2>

                {/* Note about Division 40 */}
                <div className="mb-2 p-2 pt-0 bg-yellow-100 border border-yellow-400 rounded">
                    <p className="text-sm">
                        <strong>Note:</strong> Since the ruling in 2017, Division 40 Plant & Equipment depreciation is not applicable unless the new owner incurs the cost.
                    </p>
                </div>

                {/* Construction Index and Indexed Cost */}
                <div className="">
                    <p className="text-sm">
                        <strong>Construction Year:</strong> {propertyDetails.propertyYearBuilt || 'N/A'}
                    </p>
                    <p className="text-sm">
                        <strong>Construction Index when built:</strong> {constructionIndex !== 1 ? constructionIndex : 'N/A'} (Current construction index: {latestConstructionIndex})
                    </p>
                </div>

                {/* Annual Depreciation */}
                <div>
                    <p className="text-sm mb-2">
                        Capital works are depreciated over 40 years at a rate of 2.5% per annum.
                    </p>
                    <table className="min-w-full table-auto text-sm">
                        <thead>
                            <tr className="bg-gray-400">
                                <th className="px-2 py-1 text-left rounded-tl-lg">Description</th>
                                <th className="px-2 py-1 text-right rounded-tr-lg">Amount (AUD)</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            <tr>
                                <td className="px-2 py-1">Total Depreciation Value Range</td>
                                <td className="px-2 py-1 text-right">
                                    {formatCurrency(taxDepreciationSchedule.depreciationSummary.lowEstimate.totalOriginalCost)} - {formatCurrency(taxDepreciationSchedule.depreciationSummary.highEstimate.totalOriginalCost)}
                                </td>
                            </tr>
                            <tr className="border-t">
                                <td className="px-2 py-1">Depreciation Rate</td>
                                <td className="px-2 py-1 text-right">2.5%</td>
                            </tr>
                            <tr className="font-semibold bg-gray-100">
                                <td className="px-2 py-1">Annual Depreciation Value Range</td>
                                <td className="px-2 py-1 text-right">
                                    {formatCurrency(taxDepreciationSchedule.depreciationSummary.lowEstimate.totalOriginalCost * 0.025)} - {formatCurrency(taxDepreciationSchedule.depreciationSummary.highEstimate.totalOriginalCost * 0.025)} p.a.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                

                {/* Rebuild Cost Breakdown */}
                <div className="mb-6">
                    <p className="text-sm mb-4">
                        This schedule provides a breakdown of the total rebuild cost per component and indicates whether each component is fully, partially, or excluded from the Division 43 calculation.
                    </p>

                    <table className="min-w-full table-auto text-sm mb-4">
                        <thead>
                            <tr className="bg-gray-400">
                                <th className="px-2 py-1 text-left rounded-tl-lg">Description</th>
                                <th className="px-2 py-1 text-right">Current rebuild cost (AUD)</th>
                                <th className="px-2 py-1 text-right">Indexed original cost estimate (AUD)</th>
                                <th className="px-2 py-1 text-center rounded-tr-lg">Div 43 inclusion?</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            {depreciationBreakdown.map((item, index) => (
                                <tr key={index} className="border-b">
                                    <td className="px-2 py-1">{item.item}</td>
                                    <td className="px-2 py-1 text-right">{formatCurrency(item.rebuildCost)}</td>
                                    <td className="px-2 py-1 text-right">{formatCurrency(item.originalConstructionCostEstimate)}</td>
                                    <td className="px-2 py-1 text-right">
                                        {renderInclusionStatus(item.inclusionStatus)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

            </div>
            {/* Footer Section */}
            <Footer />
        </div>
    );

};

export default TaxDepreciationSchedule;
